/**
 * How a client's identifiers are typed and shown masked (16 October). Plain TypeScript, no React, so
 * api/test/identifier-format.test.ts runs it.
 *
 * Typing:
 * - SSN: shown 3-2-4 as typed (123-45-6789), nine characters at most.
 * - Medicare ID (MBI): upper-cased, shown 4-3-4 as printed on the card (1EG4-TE5-MK73), eleven at most.
 * - Medicaid ID (CIN): upper-cased and spaces dropped, nothing else. New York prints it unbroken
 *   (AB12345C); a grouping waits on Finance's card check.
 * - ONLY spaces and dashes are ever dropped. Any other character, a letter in an SSN or an O for a zero, is
 *   KEPT where it was typed, so the server refuses the value with its usual message. A formatter that
 *   silently dropped it could turn a mistyped number into a different valid one.
 * - The caret stays after the character just typed, even when a dash is inserted before it; deleting a
 *   dash deletes the character before it.
 * What is sent is what is shown. The API drops the dashes and stores the normalised value, as before.
 */

export type IdentifierKind = 'SSN' | 'MEDICAID' | 'MEDICARE';

interface Shape { max: number | null; groups: number[]; drop: RegExp; upper: boolean }

const SHAPES: Record<IdentifierKind, Shape> = {
  SSN: { max: 9, groups: [3, 2, 4], drop: /[\s-]/g, upper: false },
  MEDICARE: { max: 11, groups: [4, 3, 4], drop: /[\s-]/g, upper: true },
  MEDICAID: { max: null, groups: [], drop: /\s/g, upper: true },
};

/** Where the dashes go: after each group but the last, once something follows it. */
function group(kind: IdentifierKind, chars: string): string {
  const { groups } = SHAPES[kind];
  let out = '';
  let at = 0;
  for (let g = 0; g < groups.length && at < chars.length; g++) {
    if (g > 0) out += '-';
    out += chars.slice(at, at + groups[g]!);
    at += groups[g]!;
  }
  return out + chars.slice(at);
}

/**
 * The text to show for what is in the field, and where the caret goes. `prev` is the text shown before this
 * change, so that deleting a dash can delete the character before it rather than nothing.
 */
export function format(kind: IdentifierKind, raw: string, caret: number, prev?: string): { text: string; caret: number } {
  const shape = SHAPES[kind];
  let s = raw;
  let c = Math.max(0, Math.min(caret, s.length));
  // A single dash deleted (Backspace after it, or Delete before it): take the character before it instead.
  if (prev !== undefined && shape.groups.length && s.length === prev.length - 1) {
    const removed = [...prev].findIndex((ch, i) => ch !== s[i]);
    const at = removed === -1 ? prev.length - 1 : removed;
    if (prev[at] === '-' && prev.slice(0, at) + prev.slice(at + 1) === s && at > 0) {
      s = s.slice(0, at - 1) + s.slice(at);
      c = at - 1;
    }
  }
  const keep = (part: string) => {
    const t = part.replace(shape.drop, '');
    return shape.upper ? t.toUpperCase() : t;
  };
  let chars = keep(s);
  if (shape.max !== null) chars = chars.slice(0, shape.max);
  // How many kept characters precede the caret; the caret goes after that many in the grouped text.
  const before = Math.min(keep(s.slice(0, c)).length, chars.length);
  const text = shape.groups.length ? group(kind, chars) : chars;
  let pos = 0;
  for (let seen = 0; pos < text.length && seen < before; pos++) if (text[pos] !== '-') seen++;
  return { text, caret: pos };
}

/** A number masked to its last four, in the same grouping as it is typed. */
export function masked(kind: IdentifierKind, last4: string): string {
  if (kind === 'SSN') return `•••-••-${last4}`;
  if (kind === 'MEDICARE') return `••••-•••-${last4}`;
  return `••••${last4}`;
}

/** The keypad a tablet should offer: numbers for the SSN, letters and numbers otherwise. */
export function inputModeFor(kind: IdentifierKind): 'numeric' | 'text' {
  return kind === 'SSN' ? 'numeric' : 'text';
}
